// Copyright (c) Microsoft. All rights reserved.

import { Persona, Text, makeStyles, makeStaticStyles, mergeClasses, shorthands } from '@fluentui/react-components';
import { ThumbDislike24Filled, ThumbLike16Filled } from '@fluentui/react-icons';
import React from 'react';
import { GetResponseOptions, useChat } from '../../../libs/hooks/useChat';
import { AuthorRoles, ChatMessageType, IChatMessage, UserFeedback } from '../../../libs/models/ChatMessage';
import { useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { FeatureKeys } from '../../../redux/features/app/AppState';
import { Breakpoints, customTokens } from '../../../styles';
import { timestampToDateString } from '../../utils/TextUtils';
import { PlanViewer } from '../plan-viewer/PlanViewer';
// import { PromptDialog } from '../prompt-dialog/PromptDialog';
import { TypingIndicator } from '../typing-indicator/TypingIndicator';
import * as utils from './../../utils/TextUtils';
import { ChatHistoryDocumentContent } from './ChatHistoryDocumentContent';
import { ChatHistoryTextContent } from './ChatHistoryTextContent';
import { UserFeedbackActions } from './UserFeedbackActions';
import { Citations } from './Citations';
import { ChatHistoryLinkTextContent } from './ChatHistoryLinkTextContent';
import { ChatContextMenu } from './ChatContextMenu';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'row',
        maxWidth: '75%',
        ...shorthands.borderRadius(customTokens.borderRadiusMedium),
        ...Breakpoints.small({
            maxWidth: '100%',
        }),
        ...shorthands.gap(customTokens.spacingHorizontalXS),
        '& img': {
            objectFit:'cover'
        }
    },
    debug: {
        position: 'absolute',
        top: '-4px',
        right: '-4px',
    },
    alignEnd: {
        alignSelf: 'flex-end',
    },
    persona: {
        paddingTop: customTokens.spacingVerticalS,
    },
    item: {
        backgroundColor: customTokens.colorNeutralBackground1,
        ...shorthands.borderRadius(customTokens.borderRadiusMedium),
        ...shorthands.padding(customTokens.spacingVerticalS, customTokens.spacingHorizontalL),
    },
    me: {
        backgroundColor: customTokens.colorBrandBackground2,
    },
    time: {
        color: customTokens.colorNeutralForeground3,
        fontSize: customTokens.fontSizeBase200,
        fontWeight: 400,
    },
    header: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        ...shorthands.gap(customTokens.spacingHorizontalL),
    },
    canvas: {
        width: '100%',
        textAlign: 'center',
    },
    image: {
        maxWidth: '250px',
    },
    blur: {
        filter: 'blur(5px)',
    },
});

const useStaticStyles = makeStaticStyles({
    '.contextCitation': {
        borderBottomWidth: '2px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'transparent',
        textDecorationStyle: 'dotted',
        textDecorationColor: '#919191',
        textUnderlineOffset: '3px',
        color: 'inherit',
    },
    '.contextCitation:hover': {
        cursor: 'pointer',
        background: '#fff4f4',
        color: '#692b61',
        textDecorationColor: '#692b61',
        borderRadius: '2px',
    },
    '.contextCitationSup': {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'text-top',
        top: '1px',
        margin: '0px 2px',
        minWidth: '14px',
        height: '14px',
        textDecorationColor: 'transparent',
        outline: 'transparent solid 1px',
        color: '#75306c',
        background: '#f6d0d0',
        borderRadius: '3px',
        fontSize: '10px',
        lineHeight: '14px',
        fontWeight: '600',
        padding: '0px 1px 0px 0px',
    },
});

interface ChatHistoryItemProps {
    message: IChatMessage;
    getResponse: (options: GetResponseOptions) => Promise<void>;
    messageIndex: number;
}

export const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({ message, getResponse, messageIndex }) => {
    const classes = useClasses();

    useStaticStyles();

    const chat = useChat();
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo, features } = useAppSelector((state: RootState) => state.app);

    const isMe = message.authorRole === AuthorRoles.User && message.userId === activeUserInfo?.id;
    const isBot = message.authorRole === AuthorRoles.Bot;
    const user = chat.getChatUserById(message.userName, selectedId, conversations[selectedId].users);
    const fullName = user?.fullName ?? message.userName;

    const avatar = isBot
        ? { image: { src: conversations[selectedId].botProfilePicture } }
        : { name: fullName, color: 'brand' as const };

    let content: JSX.Element;

    if (isBot && message.type === ChatMessageType.Plan) {
        content = <PlanViewer message={message} messageIndex={messageIndex} getResponse={getResponse} />;
    } else if (message.type === ChatMessageType.Document) {
        content = <ChatHistoryDocumentContent isMe={isMe} message={message} />;
    } else {
        content =
            isBot && message.content.length === 0 ? <TypingIndicator /> : <ChatHistoryTextContent message={message} />;
    }

    // TODO: [Issue #42] Persistent RLHF, hook up to model
    // Currently for demonstration purposes only, no feedback is actually sent to kernel / model
    const showShowRLHFMessage =
        features[FeatureKeys.RLHF].enabled &&
        message.userFeedback === UserFeedback.Requested &&
        messageIndex === conversations[selectedId].messages.length - 1 &&
        message.userId === 'bot';

    const contextCitationsIndexes = [];

    let contentWithCitations = '';

    // Include context citations
    if (isBot && message.contextCitations && message.content && message.citations) {
        const messageContent = utils.formatChatTextContent(message).split(/[ /]+/);
        const contextCitations = message.contextCitations.split('#');

        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let index = 0; index < contextCitations.length; index++) {
            const contextCitation = contextCitations[index];
            const citationRanges = contextCitation.split(';');

            contextCitationsIndexes.push(index);

            for (const citationRange of citationRanges) {
                const startIndex = parseInt(citationRange.split('-')[0]);
                const endIndex = parseInt(citationRange.split('-')[1]);
                const citationLink = message.citations[index];

                messageContent[startIndex] = `<a class="contextCitation" target="_blank" href="${citationLink}">${messageContent[startIndex]}`;
                messageContent[endIndex] = `${messageContent[endIndex]}</a><a target="_blank" href="${citationLink}"><sup class="contextCitationSup">${index + 1}</sup></a>`;
            }
        }

        contentWithCitations = messageContent.join(' ').replace(/undefined/g, '');
    }

    return (
        <div
            className={isMe ? mergeClasses(classes.root, classes.alignEnd) : classes.root}
            // The following data attributes are needed for CI and testing
            data-testid={`chat-history-item-${messageIndex}`}
            data-username={fullName}
            data-content={utils.formatChatTextContent(message)}
        >
            {
                <Persona
                    className={classes.persona}
                    avatar={avatar}
                    presence={
                        !features[FeatureKeys.SimplifiedExperience].enabled && !isMe
                            ? { status: 'available' }
                            : undefined
                    }
                />
            }
            <div className={isMe ? mergeClasses(classes.item, classes.me) : classes.item}>
                <div className={classes.header}>
                    {!isMe && <Text weight="semibold">{fullName}</Text>}
                    <Text className={classes.time}>{timestampToDateString(message.timestamp, true)}</Text>
                    <ChatContextMenu message={message} />
                    {/* {isBot && features[FeatureKeys.ShowPrompt].enabled && <PromptDialog message={message} />} */}
                </div>
                {contentWithCitations ? (
                    <ChatHistoryLinkTextContent message={{ ...message, content: contentWithCitations }} />
                ) : (
                    <div id={`chat-history-item-${message.id}`}>
                        {content}
                    </div>
                )}
                {showShowRLHFMessage && <UserFeedbackActions messageIndex={messageIndex} />}
                {isBot && message.citations && <Citations citations={utils.getUsedCitations(message)} contextCitationsIndexes={contextCitationsIndexes} />}
                {isBot && message.webCitations && (
                    <Citations citations={utils.getUsedWebCitations(message)} />
                )}
            </div>
            {showShowRLHFMessage && message.userFeedback === UserFeedback.Positive && (
                <ThumbLike16Filled color="gray" />
            )}
            {showShowRLHFMessage && message.userFeedback === UserFeedback.Negative && (
                <ThumbDislike24Filled color="gray" />
            )}
        </div>
    );
};
